import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import moment from 'moment';

import AdjustIcon from '@mui/icons-material/Adjust';
import BorderStyleIcon from '@mui/icons-material/BorderStyle';
import CloseIcon from '@mui/icons-material/Close';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import EditIcon from '@mui/icons-material/Edit';
import PentagonOutlinedIcon from '@mui/icons-material/PentagonOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Button,
  Checkbox,
  Chip,
  darken,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { ColorPicker } from 'components/atoms/ColorPicker';
import { AnnotationClassRemovalOperation, TodoOption } from 'interfaces/annotation';
import { every, find, findIndex, forEach, includes, isEmpty, map, some, times } from 'lodash';
import React from 'react';
import { getTodoClassKey } from 'services/annotations/helpers';
import useCellRulePanels from 'utils/queryHooks/cellRule/useCellRulePanels';
import { useCasesParams } from 'utils/useCasesParams';

import { buildTodoOptionRowId, getTodoOptionNameKey, TodoOptionRowSegment } from './annotationClassOptions';

interface SelectProps {
  isTodoSelected: (id: string) => boolean;
  selectedClassesToAnnotate: TodoOption[];
  onClassesSelected: (classes: TodoOption[]) => void;
}

interface ClassesToAnnotateListProps {
  classesToAnnotate: TodoOption[];
  setClassesToAnnotate?: (classes: TodoOption[]) => void;
  onDeleteTodo?: (id: string) => void;
  onColorChange?: (id: string, color: string) => void;
  isSelectable?: boolean;
  selectProps?: SelectProps;
  isEditable?: boolean;
  showWarningOnItem?: (item: TodoOption) => boolean;
  warningMessage?: string;
  classesToRemoveWithActions?: AnnotationClassRemovalOperation[];
  onEditDeleted?: (id: string) => void;
  onReplaceClass?: (id: string) => void;
  isExistingAnnotations?: (currentClassToEdit: TodoOption) => boolean;
  onRestoreDeleted?: (id: string) => void;
  alreadyDeletedOptions?: TodoOption[];
  duplicateNameKeys?: Set<string>;
}

const ClassesToAnnotateList: React.FC<ClassesToAnnotateListProps> = ({
  classesToAnnotate,
  setClassesToAnnotate,
  onDeleteTodo,
  onColorChange,
  isSelectable,
  selectProps,
  isEditable,
  showWarningOnItem,
  warningMessage,
  classesToRemoveWithActions = [],
  onEditDeleted,
  onReplaceClass,
  isExistingAnnotations,
  onRestoreDeleted,
  alreadyDeletedOptions = [],
  duplicateNameKeys = new Set<string>(),
}) => {
  if (isEditable && !onDeleteTodo) {
    console.warn(
      'ClassesToAnnotateList: onDeleteTodo is not defined but isEditable is true- (not showing delete button)'
    );
  }
  if (isEditable && !onColorChange) {
    console.warn(
      'ClassesToAnnotateList: onColorChange is not defined but isEditable is true- (not allowing to edit color)'
    );
  }
  if (isSelectable && !selectProps) {
    console.warn('ClassesToAnnotateList: selectProps is not defined but isSelectable is true- (not showing checkbox)');
  }

  const allowSelect = isSelectable && Boolean(selectProps);
  const allSelected =
    allowSelect &&
    every(classesToAnnotate, (classToAnnotate) => selectProps.isTodoSelected(getTodoClassKey(classToAnnotate)));
  const someSelected =
    allowSelect &&
    !allSelected &&
    some(classesToAnnotate, (classToAnnotate) => selectProps.isTodoSelected(getTodoClassKey(classToAnnotate)));

  const toggleSelection = selectProps
    ? (ids: string[], checked: boolean) => {
        const newSelectedClassesToAnnotate = selectProps.selectedClassesToAnnotate || [];
        forEach(ids, (id) => {
          const idSelected = includes(map(selectProps.selectedClassesToAnnotate, getTodoClassKey), id);
          if (checked) {
            if (!idSelected)
              newSelectedClassesToAnnotate.push(
                find(classesToAnnotate, (classToAnnotate) => getTodoClassKey(classToAnnotate) === id)
              );
          } else {
            newSelectedClassesToAnnotate.splice(
              newSelectedClassesToAnnotate.indexOf(
                find(newSelectedClassesToAnnotate, (classToAnnotate) => getTodoClassKey(classToAnnotate) === id)
              ),
              1
            );
          }
        });
        selectProps.onClassesSelected(newSelectedClassesToAnnotate);
      }
    : undefined;

  const checkAll = (checked: boolean) => {
    toggleSelection(map(classesToAnnotate, getTodoClassKey), checked);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activeRows = map(classesToAnnotate, (item, index) => ({
    rowId: buildTodoOptionRowId('active', index),
    segment: 'active' as TodoOptionRowSegment,
    item,
  }));
  const pendingRemovalRows = map(classesToRemoveWithActions, (entry, index) => ({
    rowId: buildTodoOptionRowId('removalPending', index),
    segment: 'removalPending' as TodoOptionRowSegment,
    item: entry.classToRemove,
  }));
  const archivedRows = map(alreadyDeletedOptions, (item, index) => ({
    rowId: buildTodoOptionRowId('archived', index),
    segment: 'archived' as TodoOptionRowSegment,
    item,
  }));
  const allRows = [...activeRows, ...pendingRemovalRows, ...archivedRows];

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const activeIndex = findIndex(activeRows, (row) => row.rowId === active?.id);
      const overIndex = findIndex(activeRows, (row) => row.rowId === over?.id);
      const newClasses = arrayMove(classesToAnnotate, activeIndex, overIndex);
      setClassesToAnnotate(newClasses);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={map(activeRows, ({ item, rowId }) => ({ ...item, id: rowId }))}
        strategy={verticalListSortingStrategy}
      >
        <List>
          {!isEmpty(classesToAnnotate) && allowSelect && (
            <ListItem>
              <ListItemIcon sx={{ minWidth: '40px' }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onClick={() => {
                    if (allSelected) {
                      checkAll(false);
                    } else {
                      checkAll(true);
                    }
                  }}
                />
              </ListItemIcon>
              <ListItemText primary={'Select All'} />
            </ListItem>
          )}

          {map(allRows, ({ rowId, segment, item: classToAnnotate }) => {
            const classKey = getTodoClassKey(classToAnnotate);
            return (
              <ClassToAnnotateListItem
                key={rowId}
                rowId={rowId}
                item={classToAnnotate}
                isDuplicate={duplicateNameKeys.has(getTodoOptionNameKey(classToAnnotate))}
                onDelete={isEditable && Boolean(onDeleteTodo) ? () => onDeleteTodo(rowId) : undefined}
                onReplaceClass={isEditable && Boolean(onReplaceClass) ? () => onReplaceClass(rowId) : undefined}
                isExistingAnnotations={isExistingAnnotations && isExistingAnnotations(classToAnnotate)}
                onColorChange={
                  isEditable && Boolean(onColorChange)
                    ? (newColor) => {
                        onColorChange(rowId, newColor);
                      }
                    : undefined
                }
                isSelectable={allowSelect}
                isSelected={selectProps?.isTodoSelected && selectProps.isTodoSelected(classKey)}
                toggleSelection={toggleSelection ? (checked) => toggleSelection([classKey], checked) : undefined}
                isEditable={isEditable}
                isDraggable={Boolean(setClassesToAnnotate) && segment === 'active'}
                showWarning={showWarningOnItem && showWarningOnItem(classToAnnotate)}
                warningMessage={warningMessage}
                isDeleted={segment !== 'active'}
                onEditDeleted={segment === 'removalPending' && onEditDeleted ? () => onEditDeleted(rowId) : undefined}
                onRestoreDeleted={onRestoreDeleted ? () => onRestoreDeleted(rowId) : undefined}
              />
            );
          })}
        </List>
      </SortableContext>
    </DndContext>
  );
};

interface ClassToAnnotateListItemProps {
  item: TodoOption;
  rowId?: string;
  onColorChange?: (color: string) => void;
  onDelete?: (event: any) => void;
  isSelectable?: boolean;
  isSelected?: boolean;
  toggleSelection?: (checked: boolean) => void;
  isEditable?: boolean;
  isDraggable?: boolean;
  showWarning?: boolean;
  warningMessage?: string;
  isDeleted?: boolean;
  isDuplicate?: boolean;
  onEditDeleted?: () => void;
  onReplaceClass?: () => void;
  isExistingAnnotations?: boolean;
  onRestoreDeleted?: () => void;
}

export const ClassToAnnotateListItem: React.FC<ClassToAnnotateListItemProps> = ({
  item,
  rowId,
  onColorChange,
  onDelete,
  isSelectable,
  isSelected,
  toggleSelection,
  isEditable,
  isDraggable = false,
  showWarning,
  warningMessage,
  isDeleted,
  isDuplicate,
  onEditDeleted,
  onReplaceClass,
  isExistingAnnotations,
  onRestoreDeleted,
}) => {
  const { className, color, displayName, name, createdAt, originPanelId } = item;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rowId ?? getTodoClassKey(item),
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  const { casesParams } = useCasesParams();
  const studyId = casesParams.studyId ?? casesParams.filters?.studyId;
  const { data: panels } = useCellRulePanels(studyId);
  const originPanelName = find(panels, { id: originPanelId })?.label;

  const handleColorChange = (newColor: string) => {
    onColorChange(newColor);
  };

  return (
    <ListItem
      style={style}
      secondaryAction={
        Boolean(onDelete) && (
          <Tooltip title="Remove">
            <Button
              onClick={(event) => onDelete(event)}
              disabled={isDeleted}
              size="small"
              variant="text"
              sx={{ minWidth: 0 }}
              color="secondary"
            >
              <CloseIcon />
            </Button>
          </Tooltip>
        )
      }
    >
      {isDraggable && (
        <ListItemIcon
          sx={{
            cursor: !isDeleted ? (isDragging ? 'grabbing' : 'grab') : 'inherit',
            minWidth: '40px',
            color: isDeleted ? 'grey' : 'inherit',
          }}
          {...(!isDeleted ? listeners : {})}
          {...(!isDeleted ? attributes : {})}
          ref={setNodeRef}
        >
          {isDeleted ? null : <DragHandleIcon />}
        </ListItemIcon>
      )}
      {isSelectable && (
        <ListItemIcon sx={{ minWidth: '40px' }}>
          <Checkbox
            checked={isSelected}
            onChange={toggleSelection ? (e) => toggleSelection(e.target.checked) : undefined}
            disabled={isDeleted}
          />
        </ListItemIcon>
      )}

      {/* the default minWidth is 56px witch is too big for this case */}
      <ListItemIcon sx={{ minWidth: '40px' }}>
        <ColorPicker
          disableEditing={!isEditable || !handleColorChange || isDeleted}
          currentColor={isDeleted && color ? darken(color, 0.5) : color}
          onColorChange={handleColorChange}
        />
      </ListItemIcon>
      <ListItemIcon sx={{ minWidth: '40px', color: isDeleted ? 'grey' : 'inherit' }}>
        {className === 'polygon' ? (
          name.includes('roi') ? (
            <BorderStyleIcon />
          ) : (
            <PentagonOutlinedIcon />
          )
        ) : (
          <AdjustIcon />
        )}
      </ListItemIcon>
      <ListItemText
        primary={
          <Grid container alignItems="center" spacing={1} justifyContent="space-between">
            <Grid item xs={8}>
              <Grid container alignItems="center" spacing={1} wrap="nowrap">
                {isDuplicate && (
                  <Grid item>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="warning"
                      label="Duplicate"
                      sx={{
                        height: 18,
                        fontSize: 11,
                      }}
                    />
                  </Grid>
                )}
                {isDeleted && (
                  <Grid item>
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Archived"
                      sx={{
                        height: 18,
                        fontSize: 11,
                      }}
                    />
                  </Grid>
                )}
                {isDeleted && isExistingAnnotations && (
                  <Grid item>
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Annotations"
                      sx={{
                        height: 18,
                        fontSize: 11,
                      }}
                    />
                  </Grid>
                )}
                <Grid item>{displayName ?? name}</Grid>
              </Grid>
            </Grid>
            <Grid item xs={4} container spacing={1} alignItems="center">
              {originPanelName && <Grid item>{originPanelName},</Grid>}
              {createdAt && <Grid item>{moment(createdAt).format('ll')}</Grid>}
            </Grid>
          </Grid>
        }
        primaryTypographyProps={{
          variant: 'subtitle2',
          style: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: isDeleted ? 'grey' : 'inherit',
          },
        }}
        secondary={`${map(item.markerPositivity, (value, key) => `${key}-${value}`).join(', ')}`}
      />
      {showWarning && (
        <Tooltip title={warningMessage}>
          <Button size="small" color="secondary" variant="text" sx={{ minWidth: 0 }}>
            <WarningAmberIcon />
          </Button>
        </Tooltip>
      )}
      {onReplaceClass && isExistingAnnotations && !isDeleted && (
        <Tooltip title="Replace class for annotations">
          <Button
            aria-label="Replace class for annotations"
            size="small"
            variant="text"
            color="secondary"
            onClick={onReplaceClass}
            sx={{ minWidth: 0 }}
          >
            <SwapHorizIcon />
          </Button>
        </Tooltip>
      )}
      {isDeleted && onEditDeleted && isExistingAnnotations && (
        <Tooltip title="Edit deleted class">
          <Button size="small" variant="text" color="secondary" onClick={onEditDeleted} sx={{ minWidth: 0 }}>
            <EditIcon />
          </Button>
        </Tooltip>
      )}
      {isDeleted && onRestoreDeleted && (
        <Tooltip title="Restore class">
          <Button
            aria-label={`Restore class ${displayName ?? name}`}
            size="small"
            color="secondary"
            variant="text"
            onClick={onRestoreDeleted}
            sx={{ minWidth: 0 }}
          >
            <UnarchiveOutlinedIcon />
          </Button>
        </Tooltip>
      )}
    </ListItem>
  );
};

export const ClassesToAnnotateListSkeleton: React.FC = () => {
  return (
    <List>
      {times(5, (index) => (
        <ListItem key={index}>
          <ListItemIcon sx={{ minWidth: '40px' }}>
            <Skeleton variant="rounded" width={15} height={15} />
          </ListItemIcon>
          <ListItemIcon sx={{ minWidth: '40px' }}>
            <Skeleton variant="rounded" width={15} height={15} />
          </ListItemIcon>
          <Skeleton variant="text" width={300} />
        </ListItem>
      ))}
    </List>
  );
};

export default ClassesToAnnotateList;
